/**
 * useOuvertureDePiece — la mécanique d'« ouvrir une pièce du dossier dans le
 * canvas », sans une ligne de JSX.
 *
 * **Ce module n'invente pas un second chemin de chargement.** Il EXTRAIT celui
 * qui existait déjà, écrit en dur dans `handleLoadDocument`
 * ([`CanvasDocumentOpenDialog.tsx`](../components/CanvasDocumentOpenDialog.tsx)),
 * pour que la dialog d'arborescence et le bouton par ligne de « Mes écritures »
 * exécutent LE MÊME code. Recopier ces trente lignes aurait produit deux gardes
 * de protection qui divergent au premier correctif — or la protection est ce qui empêche
 * d'effacer l'acte qu'Hector est en train d'écrire.
 *
 * **Pourquoi un hook sans rendu.** Le dépôt réserve `@testing-library/react`
 * aux hooks headless (les tests de composant sont des stories Storybook). Ce qui
 * mérite d'être prouvé ici est de la logique pure et coûteuse à se tromper : le
 * protection de la **cible exacte** relue DEUX fois, l'ordre prendre-puis-poser, et le refus d'un format que
 * le canvas ne sait pas ouvrir. Laissé dans le JSX, rien de cela ne serait
 * testable, donc rien ne serait testé.
 *
 * **Une pièce ouverte ici est le DOCUMENT DU DOSSIER, pas une copie anonyme.**
 * `documentId` porte l'uuid réel de la table `documents` — c'est la différence
 * avec un modèle, dont l'id ne doit jamais y arriver : ici l'agent PEUT le
 * rejouer dans `load_document_into_canvas`, et c'est voulu.
 */

import * as React from 'react';
import { useConversationCasesStore } from '@/features/hector-agent-casediscovery-v3/stores/conversationCasesStore';
import { CANVAS_REPLACEMENT_BLOCKED_MESSAGE } from '../state/canvasSessionProtection';
import type { CanvasResidentReplacementTarget } from '../state/canvasResidentPresentation';
import {
  decrireLeRefusDeSortie,
  type SortieDuDocumentCourant,
} from '../state/canvasBasculeDeBrouillon';

/**
 * Le seul format que le canvas sait ouvrir.
 *
 * SuperDoc monte un `.docx` et rien d'autre : un PDF ou une image n'a pas de
 * document Word à reconstruire. La contrainte est plus étroite que celle de
 * « Ouvrir dans Word », qui accepte `pdf | docx | doc` parce que Word, lui,
 * convertit à l'ouverture (`DocumentViewerService.canOpenInWord`). Confondre les
 * deux ferait promettre au bouton une ouverture qui échouerait au montage.
 */
export const FORMAT_OUVRABLE_DANS_LE_CANVAS = 'docx';

/**
 * La forme minimale d'une pièce que ces règles savent lire.
 *
 * Structurelle à dessein : `CaseDocumentRecord` (dialog) et les lignes de
 * `FolderGridView` (typées `any[]` côté prop) s'y assignent toutes deux sans
 * que ce module ait à connaître leur provenance.
 */
export interface PieceDuDossier {
  readonly id?: string;
  readonly name?: string;
  readonly file_path?: string | null;
  readonly enriched_title?: unknown;
  readonly document_type?: string;
  readonly is_folder?: boolean;
  readonly case_id?: string;
}

/** Ce que le hook a besoin de savoir d'une pièce — volontairement minimal. */
export interface PieceAOuvrir {
  readonly id: string;
  readonly titre: string;
  readonly cheminDuFichier: string;
  readonly caseId: string | null;
}

/** Les métadonnées posées — chaque champ est une décision, voir les commentaires. */
export interface MetaDeLaPieceOuverte {
  readonly documentId: string;
  readonly documentTitle: string;
  readonly conversationId: string | null;
  readonly caseId: string | null;
  readonly sourceFilePath: string;
}

/** L'extension réelle, lue sur le chemin de stockage avant le nom affiché. */
function extensionDe(piece: PieceDuDossier): string {
  // `name` est souvent un libellé enrichi SANS extension : c'est `file_path` qui
  // porte le format réel. L'ordre inverse classerait « Conclusions du 3 mars »
  // comme sans extension et masquerait un `.docx` parfaitement ouvrable.
  const source = (piece.file_path ?? piece.name ?? '').toString();
  return source.split('.').pop()?.toLowerCase() ?? '';
}

/**
 * Le canvas sait-il ouvrir cette pièce ?
 *
 * Reproduit à l'identique le filtre `isWordDocument` de la dialog
 * d'arborescence — c'est le même jugement, il ne doit exister qu'une fois. Une
 * synthèse est exclue : c'est un artefact généré par Hector rangé à côté des
 * écritures, pas un document que l'avocat rédige.
 *
 * ⚠️ Ne dit RIEN du chemin de fichier : la dialog liste des pièces sans chemin
 * et ne le découvre qu'au clic (message dédié). Le bouton par ligne, lui, exige
 * en plus `decrirePiecePourLeCanvas` — il ne doit pas s'afficher pour ne rien
 * ouvrir.
 */
export function laPieceSOuvreDansLeCanvas(piece: PieceDuDossier): boolean {
  if (piece.is_folder === true) return false;
  if (piece.document_type === 'synthesis') return false;
  return extensionDe(piece) === FORMAT_OUVRABLE_DANS_LE_CANVAS;
}

/** Le titre montré à l'avocat : le libellé enrichi s'il existe, sinon le nom. */
export function titreDeLaPiece(piece: PieceDuDossier): string {
  const enrichi = typeof piece.enriched_title === 'string' ? piece.enriched_title : null;
  return enrichi?.trim() || piece.name || 'Document Word';
}

/**
 * Décrire une pièce réellement ouvrable, ou `null`.
 *
 * `null` couvre les quatre refus : ce n'est pas un `.docx`, c'est un dossier,
 * c'est une synthèse, ou il n'y a aucun chemin de stockage à télécharger. Le
 * bouton par ligne s'affiche SSI cette fonction rend un objet — c'est ce qui
 * garantit qu'il ne promet jamais une ouverture impossible.
 */
export function decrirePiecePourLeCanvas(
  piece: PieceDuDossier,
  caseIdParDefaut: string | null = null,
): PieceAOuvrir | null {
  if (!laPieceSOuvreDansLeCanvas(piece)) return null;
  if (typeof piece.id !== 'string' || piece.id.length === 0) return null;
  const chemin = typeof piece.file_path === 'string' ? piece.file_path.trim() : '';
  if (chemin.length === 0) return null;
  return {
    id: piece.id,
    titre: titreDeLaPiece(piece),
    cheminDuFichier: chemin,
    caseId: typeof piece.case_id === 'string' ? piece.case_id : caseIdParDefaut,
  };
}

/**
 * Construire les métadonnées d'une pièce posée dans le canvas.
 *
 * Exportée et testée séparément : ces décisions sont invisibles à la lecture du
 * JSX et chacune casse quelque chose de différent si elle change.
 */
export function metaDeLaPiece(
  piece: PieceAOuvrir,
  porteur: string,
): MetaDeLaPieceOuverte {
  return {
    // L'uuid RÉEL de `documents`, contrairement à un modèle ouvert en copie de
    // travail : ici l'agent doit pouvoir rejouer cet id dans
    // `load_document_into_canvas` pour retrouver la même pièce.
    documentId: piece.id,
    documentTitle: piece.titre,
    conversationId: porteur,
    caseId: piece.caseId,
    // NON-NULL, et c'est la décision qui porte. Une écriture déjà versée au
    // dossier contient le papier à en-tête du cabinet ; ce champ fait renoncer
    // la pose automatique, qui sinon en ajouterait un second.
    sourceFilePath: piece.cheminDuFichier,
  };
}

/** Le mime d'un `.docx` — un blob servi sans type ferait échouer SuperDoc. */
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export const PIECE_CHARGEE_DANS_LE_CANVAS = 'Document Word chargé dans le canvas';
export const CHARGEMENT_DE_LA_PIECE_IMPOSSIBLE = 'Chargement du document impossible';

/** Les frontières du geste : réseau, prise du canvas, store, toasts, protection exacte. */
export interface DependancesDOuvertureDePiece {
  /**
   * Télécharge les octets par le PROXY backend, jamais par l'URL signée S3 : le
   * bucket OVH ne sert aucun en-tête CORS, un `fetch` navigateur sur l'URL
   * signée échouerait. La route backend est same-origin, authentifiée au JWT, et
   * revérifie l'accès cabinet depuis le chemin avant de servir le blob.
   */
  readonly telechargerLeFichier: (chemin: string) => Promise<Blob>;
  /** Attend puis capture la session exacte du porteur, déjà réservée par le geste humain. */
  readonly capturerCibleDuCanvas: (porteur: string) => Promise<CanvasResidentReplacementTarget>;
  /**
   * Déclare à qui le document va appartenir, et — pour les chemins qui viennent
   * d'ailleurs que le canvas — PREND le canvas au passage.
   *
   * ⚠️ Appelée APRÈS le téléchargement et AVANT la pose, et cet ordre est
   * load-bearing : la transition de résidence enregistre le brouillon sortant.
   * Poser d'abord ferait enregistrer le document QU'ON VIENT DE POSER
   * sous l'identité du précédent — l'acte en cours serait remplacé par celui-ci
   * dans son propre brouillon.
   *
   * ⚠️ Elle LÈVE quand aucun fil confirmé n'est visé (#1331) : un document sans
   * propriétaire enregistrait son brouillon sous `conversationId: null`, que
   * personne ne rouvrait jamais. Le `catch` du geste convertit l'exception en
   * toast — donc aucun téléchargement, aucune pose, aucune navigation.
   *
   * `filVise` est le fil que l'avocat a CHOISI pour ce geste (menu d'une ligne
   * de « Mes écritures »), ou `null` quand l'appelant n'en connaît qu'un — la
   * dialog d'arborescence, qui vit déjà dans son fil.
   */
  readonly designerLePorteur: (filVise: string | null) => string | Promise<string>;
  /** Commit atomique de la pièce téléchargée dans la cible précédemment capturée. */
  readonly poserDansLeCanvas: (
    target: CanvasResidentReplacementTarget,
    blob: Blob,
    meta: MetaDeLaPieceOuverte,
  ) => boolean;
  /**
   * La cible capturée est-elle protégée par un travail documentaire ?
   *
   * Le hook ne doit jamais déduire cette réponse du résident visible : la pièce
   * peut être ouverte depuis B pendant qu'A continue à rédiger en arrière-plan.
   */
  readonly canvasProtectedByWork: (target: CanvasResidentReplacementTarget) => boolean;
  /**
   * Enregistre le document SORTANT et vérifie qu'il reste de la place dans le
   * fil — `preparerLaSortieDuDocumentCourant` avec `creeUnDocument: true`.
   *
   * ⚠️ Requise, sans défaut (2026-09-23) : la pièce ouverte devient un document
   * NEUF du fil, et le détachement qui suit la pose avorterait tout
   * enregistrement du sortant encore en vol.
   */
  readonly preparerLaSortie: (porteur: string) => Promise<SortieDuDocumentCourant>;
  /**
   * Retire au contrôleur d'enregistrement l'identité du document sortant, JUSTE
   * APRÈS la pose — `detacherLeDocumentCourant`.
   *
   * ⚠️ Requise, sans défaut (2026-09-23). Le contrôleur survit au remplacement :
   * sans ce geste, la pièce s'enregistrait sous l'identifiant du document que
   * l'avocat avait ouvert, qui était ÉCRASÉ — et la purge de sa version
   * précédente sur S3 en détruisait la seule copie. Avant le signal `ready` du
   * nouveau montage, sinon le checkpoint initial de la pièce s'arme encore sous
   * l'ancienne identité.
   */
  readonly detacherLeDocumentCourant: (porteur: string) => void;
  readonly annoncerLeSucces: (texte: string) => void;
  readonly annoncerLEchec: (texte: string) => void;
}

/** Ce que l'ouverture a produit — le porteur sert à savoir où naviguer. */
export type ResultatDOuverture =
  | { readonly pose: false }
  | { readonly pose: true; readonly porteur: string };

const RIEN_POSE: ResultatDOuverture = { pose: false };

export interface OuvertureDePiece {
  readonly enCours: boolean;
  readonly ouvrir: (piece: PieceAOuvrir, filVise?: string) => Promise<ResultatDOuverture>;
}

/**
 * Ouvrir une pièce du dossier dans le canvas.
 *
 * @param deps - les frontières du geste, mémoïsées par l'appelant (elles entrent
 *   dans le `useCallback` : un objet recréé à chaque rendu ferait perdre la
 *   stabilité d'`ouvrir`).
 */
export function useOuvertureDePiece(deps: DependancesDOuvertureDePiece): OuvertureDePiece {
  const [enCours, setEnCours] = React.useState(false);
  // Le drapeau de ré-entrance vit dans une ref ET dans l'état : l'état peint le
  // composant, la ref garde la décision juste même entre deux rendus — deux
  // clics rapprochés arrivent avant que React n'ait repeint. Il porte aussi le
  // cas du DOCUMENT VOLUMINEUX : le téléchargement dure des secondes, et sans
  // lui l'avocat qui reclique lance un second chargement concurrent.
  const enCoursRef = React.useRef(false);

  const ouvrir = React.useCallback(
    async (piece: PieceAOuvrir, filVise?: string): Promise<ResultatDOuverture> => {
      if (enCoursRef.current) return RIEN_POSE;
      enCoursRef.current = true;
      setEnCours(true);
      try {
        // Réserver puis monter avant TOUTE garde donne à ce geste une session
        // précise. Une lecture globale ici refuserait B parce qu'A écrit ; la
        // cible reste ensuite immuable pendant le réseau.
        const porteur = await deps.designerLePorteur(filVise ?? null);
        const target = await deps.capturerCibleDuCanvas(porteur);
        if (deps.canvasProtectedByWork(target)) {
          deps.annoncerLEchec(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
          return RIEN_POSE;
        }
        const blob = await deps.telechargerLeFichier(piece.cheminDuFichier);
        // RE-CONTRÔLE APRÈS L'ATTENTE : télécharger une écriture de plusieurs Mo
        // prend des secondes, pendant lesquelles la rédaction a pu démarrer.
        // Sans ce second appel, la garde d'entrée ne protège que l'instant du
        // clic, pas celui de l'écriture.
        if (deps.canvasProtectedByWork(target)) {
          deps.annoncerLEchec(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
          return RIEN_POSE;
        }
        // Le document SORTANT est enregistré APRÈS le téléchargement : c'est la
        // dernière attente du geste, et plus rien ne peut s'intercaler entre
        // elle et la pose — une frappe faite pendant le téléchargement est donc
        // enregistrée elle aussi.
        const sortie = await deps.preparerLaSortie(porteur);
        if (!sortie.ok) {
          deps.annoncerLEchec(decrireLeRefusDeSortie(sortie));
          return RIEN_POSE;
        }
        if (deps.canvasProtectedByWork(target)) {
          deps.annoncerLEchec(CANVAS_REPLACEMENT_BLOCKED_MESSAGE);
          return RIEN_POSE;
        }
        // Un blob servi sans type (ou avec `application/octet-stream`) fait
        // échouer le montage SuperDoc, qui décide du parseur sur le mime.
        const docx = blob.type === DOCX_MIME ? blob : new Blob([blob], { type: DOCX_MIME });
        if (!deps.poserDansLeCanvas(target, docx, metaDeLaPiece(piece, porteur))) {
          deps.annoncerLEchec('Le canvas a changé pendant le chargement du document. Réessaie depuis la conversation concernée.');
          return RIEN_POSE;
        }
        // Dans la MÊME tâche que la pose : le signal `ready` du nouveau montage
        // ne peut pas encore être arrivé.
        deps.detacherLeDocumentCourant(porteur);
        // La pièce est sous les yeux de l'avocat et l'agent va y travailler : son
        // dossier entre dans la conversation au message suivant (spec #2592). Une
        // pièce d'un dossier déjà listé ne change rien : ce geste n'est pas envoyé.
        if (piece.caseId) useConversationCasesStore.getState().ajouter(porteur, piece.caseId);
        deps.annoncerLeSucces(PIECE_CHARGEE_DANS_LE_CANVAS);
        return { pose: true, porteur };
      } catch (err) {
        deps.annoncerLEchec(
          err instanceof Error ? err.message : CHARGEMENT_DE_LA_PIECE_IMPOSSIBLE,
        );
        return RIEN_POSE;
      } finally {
        enCoursRef.current = false;
        setEnCours(false);
      }
    },
    [deps],
  );

  return { enCours, ouvrir };
}
